<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'

export type RenderEngine = 'g6' | 'vis'
export type ToolbarTheme = 'light' | 'dark'

interface Props {
  layoutType?: 'force' | 'circular'
  /** 当前是否为编辑模式（由父组件传入） */
  isEditing?: boolean
  /** 是否处于正在保存状态 */
  isSaving?: boolean
  /** 渲染引擎类型（用于切换 G6 / VisNetwork） */
  renderEngine?: RenderEngine
  /** 工具栏主题 */
  theme?: ToolbarTheme
}

const props = withDefaults(defineProps<Props>(), {
  layoutType: 'force',
  isEditing: false,
  isSaving: false,
  renderEngine: 'vis',
  theme: 'light'
})

const emit = defineEmits<{
  /** 点击「编辑」：请求进入编辑模式 */
  'toggle-edit': []
  /** 点击「保存」：请求保存 */
  save: []
  /** 点击「取消」：请求取消编辑 */
  cancel: []
  /** 图谱填充到视口 */
  fitView: []
  /** 图谱重置布局 */
  resetLayout: []
  /** 切换到力导向布局 */
  switchToForce: []
  /** 切换到环形布局 */
  switchToCircular: []
  /** 在视口中心添加节点 */
  'add-node': []
  /** 添加节点间的关系 */
  'add-relation': []
  /** 渲染引擎切换（v-model:renderEngine） */
  'update:renderEngine': [value: RenderEngine]
  /** 主题切换（v-model:theme） */
  'update:theme': [value: ToolbarTheme]
}>()

const editMode = computed(() => props.isEditing)

const handleToggleEdit = () => {
  if (props.isEditing) return
  emit('toggle-edit')
}

const handleAddNode = () => {
  if (!props.isEditing) {
    ElMessage.warning('请先进入编辑模式')
    return
  }
  emit('add-node')
}

const handleAddRelation = () => {
  if (!props.isEditing) {
    ElMessage.warning('请先进入编辑模式')
    return
  }
  emit('add-relation')
}

// ==================== 布局切换 ====================
const layoutType = ref(props.layoutType)
const collapsed = ref(false)

// ==================== 渲染引擎切换 ====================
const engine = ref<RenderEngine>(props.renderEngine)
const themeMode = ref<ToolbarTheme>(props.theme)
watch(
  () => props.renderEngine,
  (v) => {
    engine.value = v
  }
)
watch(
  () => props.theme,
  (v) => {
    themeMode.value = v
  }
)

const handleSwitchEngine = (v: RenderEngine) => {
  engine.value = v
  emit('update:renderEngine', v)
}

const toggleTheme = () => {
  const next: ToolbarTheme = themeMode.value === 'light' ? 'dark' : 'light'
  themeMode.value = next
  emit('update:theme', next)
}

const handleSwitchToForce = () => {
  layoutType.value = 'force'
  emit('switchToForce')
}

const handleSwitchToCircular = () => {
  layoutType.value = 'circular'
  emit('switchToCircular')
}

const toggleCollapse = () => {
  collapsed.value = !collapsed.value
}
</script>

<template>
  <div class="kg-toolbar" :class="{ 'kg-collapsed': collapsed, 'kg-theme-dark': themeMode === 'dark' }">
    <button @click="toggleCollapse" class="kg-btn kg-toggle" :title="collapsed ? '展开工具栏' : '收起工具栏'">
      {{ collapsed ? '▶' : '◀' }}
    </button>
    <div class="kg-btn-group" v-show="!collapsed">
      <!-- 渲染引擎切换（仅普通模式显示，避免编辑时切换导致状态丢失） -->
      <div class="kg-btn-subgroup" v-if="!editMode">
        <button
          @click="handleSwitchEngine('vis')"
          class="kg-btn"
          :class="{ 'kg-active': engine === 'vis' }"
          title="使用 VisNetwork 渲染"
        >
          Vis
        </button>
        <button
          @click="handleSwitchEngine('g6')"
          class="kg-btn"
          :class="{ 'kg-active': engine === 'g6' }"
          title="使用 AntV G6 渲染"
        >
          G6
        </button>
      </div>
      <!-- 编辑模式按钮组 -->
      <div class="kg-btn-subgroup" v-if="editMode && engine === 'g6'">
        <button @click="emit('save')" class="kg-btn kg-btn-save" title="保存所有更改">
          <span class="kg-icon">&#x1F4BE;</span>
          保存
        </button>
        <button @click="emit('cancel')" class="kg-btn kg-btn-cancel" title="放弃所有更改">
          <span class="kg-icon">&#x2715;</span>
          取消
        </button>
      </div>
      <!-- 普通模式：编辑按钮 -->
      <button
        v-if="!editMode && engine === 'g6'"
        @click="handleToggleEdit"
        class="kg-btn kg-btn-edit"
        title="进入编辑模式"
      >
        <span class="kg-icon">&#x270F;</span>
        编辑
      </button>
      <!-- 视图操作按钮（仅普通模式显示） -->
      <template v-if="!editMode">
        <button @click="toggleTheme" class="kg-btn" :title="themeMode === 'dark' ? '切换到浅色主题' : '切换到深色主题'">
          <span class="kg-icon">{{ themeMode === 'dark' ? '☀' : '🌙' }}</span>
          {{ themeMode === 'dark' ? '浅色' : '深色' }}
        </button>
        <button @click="emit('fitView')" class="kg-btn">
          <span class="kg-icon">&#x229E;</span>
          适应画布
        </button>
        <button @click="emit('resetLayout')" class="kg-btn">
          <span class="kg-icon">&#x21BB;</span>
          重置布局
        </button>
        <!-- VisNetwork 不支持 G6 的布局切换：仅 G6 模式显示 -->
        <template v-if="engine === 'g6'">
          <button
            @click="handleSwitchToForce"
            class="kg-btn"
            :class="{ 'kg-active': layoutType === 'force' }"
            title="力导向布局，节点更分散"
          >
            <span class="kg-icon">&#x26A1;</span>
            力导向
          </button>
          <button
            @click="handleSwitchToCircular"
            class="kg-btn"
            :class="{ 'kg-active': layoutType === 'circular' }"
            title="环形布局，适合枢纽型图谱"
          >
            <span class="kg-icon">&#x25CE;</span>
            环形
          </button>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.kg-toolbar {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  z-index: 100;
  display: flex;
  gap: 6px;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  font-family: 'Segoe UI', 'PingFang SC', system-ui, sans-serif;
}

.kg-toolbar:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.kg-toolbar.kg-collapsed {
  padding: 6px;
  right: 12px;
}

.kg-btn-group {
  display: flex;
  gap: 8px;
}

.kg-btn-subgroup {
  display: flex;
  gap: 6px;
  padding-right: 8px;
  border-right: 1px solid #e2e8f0;
  margin-right: 2px;
}

.kg-btn {
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  outline: none;
}

.kg-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.kg-btn.kg-active {
  background: #3b82f6;
  color: #fff;
}

/* 编辑按钮 - 蓝色 */
.kg-btn-edit {
  color: #3b82f6;
  font-weight: 600;
}

.kg-btn-edit:hover {
  background: #dbeafe;
  color: #1d4ed8;
}

/* 保存按钮 - 绿色 */
.kg-btn-save {
  background: #10b981;
  color: #fff;
  font-weight: 600;
}

.kg-btn-save:hover {
  background: #059669;
  color: #fff;
}

/* 取消按钮 - 红色 */
.kg-btn-cancel {
  background: #ef4444;
  color: #fff;
  font-weight: 600;
}

.kg-btn-cancel:hover {
  background: #dc2626;
  color: #fff;
}

.kg-icon {
  font-size: 13px;
  line-height: 1;
}

.kg-toggle {
  padding: 4px 8px;
  background: transparent;
  color: #64748b;
  border: none;
  border-radius: 4px;
  font-size: 11px;
}

.kg-toggle:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.kg-toolbar.kg-theme-dark {
  background: #0f172a;
  border-color: #334155;
  box-shadow: 0 8px 20px rgba(2, 6, 23, 0.45);
}

.kg-toolbar.kg-theme-dark .kg-btn-subgroup {
  border-right-color: #334155;
}

.kg-toolbar.kg-theme-dark .kg-btn {
  color: #cbd5e1;
}

.kg-toolbar.kg-theme-dark .kg-btn:hover {
  background: #1e293b;
  color: #f8fafc;
}

.kg-toolbar.kg-theme-dark .kg-toggle {
  color: #94a3b8;
}

.kg-toolbar.kg-theme-dark .kg-toggle:hover {
  background: #1e293b;
  color: #f8fafc;
}

.kg-toolbar.kg-theme-dark .kg-btn-edit {
  color: #60a5fa;
}

.kg-toolbar.kg-theme-dark .kg-btn-edit:hover {
  background: rgba(59, 130, 246, 0.18);
  color: #93c5fd;
}
</style>
